import { useState } from "react";
import {
  useAppSelector,
  useGetDroneStatusEndpointQuery,
  useGetListVideoStreamsEndpointQuery,
  type GetListVideoStreamsEndpointApiResponse,
} from "@/store";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  IconButton,
  Text,
} from "@chakra-ui/react";
import {
  Battery,
  Wifi,
  Compass,
  MapPin,
  X,
  Video,
} from "lucide-react";
import { VideoPlayer } from "./VideoPlayer";
import { VideoModal } from "./VideoModal";

const POLL_INTERVAL_MS = 2000;

interface TelemetryPanelProps {
  onClose: () => void;
}

export function TelemetryPanel({ onClose }: TelemetryPanelProps) {
  const selectedDroneId = useAppSelector((state) => state.fleet.selectedDroneId);
  type VideoStream = GetListVideoStreamsEndpointApiResponse["streams"][number];
  const [expandedStream, setExpandedStream] = useState<VideoStream | null>(null);

  // Fetch drone status (info + telemetry)
  const { data: droneStatus } = useGetDroneStatusEndpointQuery(
    { droneId: selectedDroneId ?? "" },
    { pollingInterval: POLL_INTERVAL_MS, skip: !selectedDroneId }
  );

  // Fetch video streams for selected drone
  const { data: videoStreamsData } = useGetListVideoStreamsEndpointQuery(
    { droneId: selectedDroneId ?? "" },
    { pollingInterval: POLL_INTERVAL_MS * 5, skip: !selectedDroneId }
  );

  if (!selectedDroneId || !droneStatus) {
    return null;
  }

  const { drone, telemetry, connected } = droneStatus;
  const droneName = drone?.model ?? selectedDroneId;
  const droneModel = drone?.manufacturer ?? "Unknown";

  return (
    <Card.Root display="flex" flexDirection="column" bg="bg.subtle" shadow="lg" maxH="full" overflow="hidden">
      <Card.Header pb="2">
        <Flex align="center" justify="space-between">
          <Heading as="h3" size="md">{droneName}</Heading>
          <Flex align="center" gap="2">
            <Badge colorPalette={connected ? "green" : "red"}>
              {connected ? "Online" : "Offline"}
            </Badge>
            <IconButton
              aria-label="Close panel"
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X style={{ height: 16, width: 16 }} />
            </IconButton>
          </Flex>
        </Flex>
        <Text fontSize="sm" color="fg.muted">{droneModel}</Text>
      </Card.Header>

      <Card.Body flex="1" overflow="auto" pt="0">
        <Flex direction="column" gap="4">
          {/* Position */}
          {telemetry?.position && (
            <TelemetrySection title="Position" icon={<MapPin style={{ height: 16, width: 16 }} />}>
              <TelemetryRow label="Latitude" value={telemetry.position.latitude.toFixed(6)} />
              <TelemetryRow label="Longitude" value={telemetry.position.longitude.toFixed(6)} />
              <TelemetryRow label="Altitude" value={`${telemetry.position.altitude.toFixed(1)} m`} />
            </TelemetrySection>
          )}

          {/* Attitude */}
          {telemetry?.attitude && (
            <TelemetrySection title="Attitude" icon={<Compass style={{ height: 16, width: 16 }} />}>
              <TelemetryRow label="Roll" value={`${telemetry.attitude.roll.toFixed(1)}°`} />
              <TelemetryRow label="Pitch" value={`${telemetry.attitude.pitch.toFixed(1)}°`} />
              <TelemetryRow label="Yaw" value={`${telemetry.attitude.yaw.toFixed(1)}°`} />
            </TelemetrySection>
          )}

          {/* Battery */}
          {telemetry?.battery && (
            <TelemetrySection title="Battery" icon={<Battery style={{ height: 16, width: 16 }} />}>
              <TelemetryRow
                label="Level"
                value={
                  <Badge
                    colorPalette={
                      telemetry.battery.percentage < 20
                        ? "red"
                        : telemetry.battery.percentage < 50
                          ? "orange"
                          : "green"
                    }
                  >
                    {telemetry.battery.percentage.toFixed(0)}%
                  </Badge>
                }
              />
              <TelemetryRow label="Voltage" value={`${telemetry.battery.voltage.toFixed(1)} V`} />
            </TelemetrySection>
          )}

          {/* Signal */}
          {telemetry?.signal && (
            <TelemetrySection title="Signal" icon={<Wifi style={{ height: 16, width: 16 }} />}>
              <TelemetryRow label="Strength" value={`${telemetry.signal.strength?.toFixed(0) ?? "—"}%`} />
              <TelemetryRow label="Available" value={telemetry.signal.available ? "Yes" : "No"} />
            </TelemetrySection>
          )}

          {/* Video Streams */}
          {videoStreamsData?.streams && videoStreamsData.streams.length > 0 && (
            <TelemetrySection title="Video" icon={<Video style={{ height: 16, width: 16 }} />}>
              <Flex direction="column" gap="2">
                {videoStreamsData.streams.map((stream) => (
                  <Box key={stream.streamId} h="120px">
                    <VideoPlayer
                      playbackUri={stream.playbackUri}
                      streamName={stream.name}
                      onExpand={() => setExpandedStream(stream)}
                    />
                  </Box>
                ))}
              </Flex>
            </TelemetrySection>
          )}
        </Flex>
      </Card.Body>

      {expandedStream && (
        <VideoModal
          stream={expandedStream}
          onClose={() => setExpandedStream(null)}
        />
      )}
    </Card.Root>
  );
}

interface TelemetrySectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function TelemetrySection({ title, icon, children }: TelemetrySectionProps) {
  return (
    <Box>
      <Flex align="center" gap="2" fontSize="sm" fontWeight="medium" mb="1">
        {icon}
        {title}
      </Flex>
      <Flex direction="column" gap="1" pl="6">
        {children}
      </Flex>
    </Box>
  );
}

interface TelemetryRowProps {
  label: string;
  value: React.ReactNode;
}

function TelemetryRow({ label, value }: TelemetryRowProps) {
  return (
    <Flex justify="space-between" fontSize="sm">
      <Text color="fg.muted">{label}</Text>
      <Text fontFamily="mono">{value}</Text>
    </Flex>
  );
}
